import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import BreadcrumbNavigation from '../../components/BreadcrumbNavigation';
import QuickActionToolbar from '../../components/QuickActionToolbar';
import Icon from '../../components/AppIcon';
import ClientFilters from './components/ClientFilters';
import ClientGrid from './components/ClientGrid';
import ClientDetails from './components/ClientDetails';
import NewClientModal from './components/NewClientModal';
import DealsGrid from './components/DealsGrid';
import DealDetails from './components/DealDetails';
import DealEntryModal from './components/DealEntryModal';
import NewSupplierModal from './components/NewSupplierModal';
import { clientService } from '../../services/clientService';
import { negotiationService } from '../../services/negotiationService';
import { supplierService } from '../../services/supplierService';

const ClientManagementSystem = () => {
  const [activeSubTab, setActiveSubTab] = useState('info');
  const [clients, setClients] = useState([]);
  const [deals, setDeals] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [isNewClientModalOpen, setIsNewClientModalOpen] = useState(false);
  const [isNewSupplierModalOpen, setIsNewSupplierModalOpen] = useState(false);
  const [isDealEntryModalOpen, setIsDealEntryModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    search: '',
    status: [],
    destination: [],
    payment: []
  });
  const [editingDeal, setEditingDeal] = useState(null);
  const [loading, setLoading] = useState(false);

  // ULTRA-SIMPLIFIED: Single data load without callbacks
  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsData, dealsData, suppliersData] = await Promise.all([
          clientService?.getAll(),
          negotiationService?.getAll(),
          supplierService?.getAll()
        ]);
        
        setClients(clientsData || []);
        setFilteredClients(clientsData || []);
        setDeals(dealsData || []);
        setSuppliers(suppliersData || []);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    
    loadData();
  }, []);

  // SIMPLIFIED: Direct filter without useEffect
  const applyFilters = () => {
    let filtered = clients;

    if (activeFilters?.search) {
      const search = activeFilters?.search?.toLowerCase();
      filtered = filtered?.filter(c => 
        c?.name?.toLowerCase()?.includes(search) ||
        c?.email?.toLowerCase()?.includes(search) ||
        c?.cpf?.includes(search)
      );
    }

    if (activeFilters?.status?.length > 0) {
      filtered = filtered?.filter(c => activeFilters?.status?.includes(c?.status));
    }

    return filtered;
  };

  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);
    setFilteredClients(applyFilters());
  };

  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
  };

  const handleDealSelect = (dealId) => {
    setSelectedDealId(dealId);
  };

  const handleNewClient = () => {
    setIsNewClientModalOpen(true);
  };

  const handleNewDeal = () => {
    setEditingDeal(null);
    setIsDealEntryModalOpen(true);
  };

  const handleEditDeal = (deal) => {
    setEditingDeal(deal);
    setIsDealEntryModalOpen(true);
  };

  const handleDealSave = async (dealData) => {
    try {
      setLoading(true);
      
      if (editingDeal) {
        await negotiationService?.update(editingDeal?.id, {
          title: dealData?.title,
          clientId: dealData?.clientId,
          destination: dealData?.destination,
          saleValue: dealData?.value,
          supplierCost: dealData?.supplierCost,
          saleDate: dealData?.saleDate,
          paymentStatus: dealData?.isPaid ? 'paid' : 'pending',
          notes: dealData?.notes
        });
      } else {
        await negotiationService?.create({
          title: dealData?.title,
          clientId: dealData?.clientId,
          destination: dealData?.destination,
          saleValue: dealData?.value,
          supplierCost: dealData?.supplierCost,
          saleDate: dealData?.saleDate,
          paymentStatus: dealData?.isPaid ? 'paid' : 'pending',
          notes: dealData?.notes
        });
      }
      
      const dealsData = await negotiationService?.getAll();
      setDeals(dealsData || []);
      setIsDealEntryModalOpen(false);
      setEditingDeal(null);
    } catch (err) {
      console.error('Error saving deal:', err);
      alert(`Erro: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNewClient = async (newClient) => {
    try {
      setLoading(true);
      
      await clientService?.create({
        name: newClient?.name,
        email: newClient?.email,
        phone: newClient?.phone,
        document: newClient?.document,
        address: newClient?.address,
        cpf: newClient?.cpf,
        birthDate: newClient?.birthDate,
        passportNumber: newClient?.passportNumber,
        passportValidity: newClient?.passportValidity,
        notes: newClient?.notes
      });
      
      const clientsData = await clientService?.getAll();
      setClients(clientsData || []);
      setFilteredClients(clientsData || []);
      setIsNewClientModalOpen(false);
      
      setTimeout(() => setIsDealEntryModalOpen(true), 100);
    } catch (err) {
      console.error('Error saving client:', err);
      alert(`Erro: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNewSupplier = async (newSupplier) => {
    try {
      setLoading(true);
      
      await supplierService?.create({
        name: newSupplier?.name,
        email: newSupplier?.email,
        phone: newSupplier?.phone,
        cnpj: newSupplier?.cnpj,
        razaoSocial: newSupplier?.razaoSocial,
        address: newSupplier?.address,
        notes: newSupplier?.notes
      });
      
      const suppliersData = await supplierService?.getAll();
      setSuppliers(suppliersData || []);
      setIsNewSupplierModalOpen(false);
      
      setTimeout(() => setIsDealEntryModalOpen(true), 100);
    } catch (err) {
      console.error('Error saving supplier:', err);
      alert(`Erro: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const selectedClient = clients?.find(c => c?.id === selectedClientId);
  const selectedDeal = deals?.find(d => d?.id === selectedDealId);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {loading && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        </div>
      )}
      <div className="main-content">
        <BreadcrumbNavigation />
        
        <div className="border-b border-border bg-card px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Icon name="Users" size={22} className="text-primary" />
            </div>
            <h1 className="text-xl font-bold text-foreground">Clientes</h1>
          </div>
        </div>

        <div className="border-b border-border bg-card/50">
          <div className="flex gap-2 px-6 pl-20">
            <button
              onClick={() => setActiveSubTab('info')}
              className={`px-5 py-3 text-sm font-medium transition-all relative rounded-t-lg ${
                activeSubTab === 'info' ? 'text-primary bg-card shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-card/50'
              }`}
            >
              <span className="flex items-center gap-2">
                <Icon name="UserCircle" size={16} />
                Informações
              </span>
              {activeSubTab === 'info' && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary rounded-t" />
              )}
            </button>
            <button
              onClick={() => setActiveSubTab('deals')}
              className={`px-5 py-3 text-sm font-medium transition-all relative rounded-t-lg ${
                activeSubTab === 'deals' ? 'text-primary bg-card shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-card/50'
              }`}
            >
              <span className="flex items-center gap-2">
                <Icon name="TrendingUp" size={16} />
                Negociação
              </span>
              {activeSubTab === 'deals' && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary rounded-t" />
              )}
            </button>
          </div>
        </div>

        <div className="h-[calc(100vh-240px)] flex">
          {activeSubTab === 'info' ? (
            <>
              <div className="w-1/4 min-w-[300px]">
                <ClientFilters
                  onFilterChange={handleFilterChange}
                  activeFilters={activeFilters}
                />
              </div>
              <div className="flex-1">
                <ClientGrid
                  clients={filteredClients}
                  onClientSelect={handleClientSelect}
                  selectedClientId={selectedClientId}
                  onBulkAction={() => {}}
                  onNewClient={handleNewClient}
                />
              </div>
              <div className="w-1/4 min-w-[350px]">
                <ClientDetails
                  client={selectedClient}
                  onClose={() => setSelectedClientId(null)}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex-1">
                <DealsGrid
                  deals={deals}
                  onDealSelect={handleDealSelect}
                  selectedDealId={selectedDealId}
                  onNewDeal={handleNewDeal}
                />
              </div>
              <div className="w-1/3 min-w-[400px]">
                <DealDetails
                  deal={selectedDeal}
                  onClose={() => setSelectedDealId(null)}
                  onEdit={handleEditDeal}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <QuickActionToolbar />
      
      <NewClientModal
        isOpen={isNewClientModalOpen}
        onClose={() => setIsNewClientModalOpen(false)}
        onSave={handleSaveNewClient}
      />

      <NewSupplierModal
        isOpen={isNewSupplierModalOpen}
        onClose={() => setIsNewSupplierModalOpen(false)}
        onSave={handleSaveNewSupplier}
      />

      <DealEntryModal
        isOpen={isDealEntryModalOpen}
        onClose={() => {
          setIsDealEntryModalOpen(false);
          setEditingDeal(null);
        }}
        onSave={handleDealSave}
        clients={clients}
        suppliers={suppliers}
        deal={editingDeal}
        onOpenClientModal={() => {
          setIsDealEntryModalOpen(false);
          setIsNewClientModalOpen(true);
        }}
        onOpenSupplierModal={() => {
          setIsDealEntryModalOpen(false);
          setIsNewSupplierModalOpen(true);
        }}
      />
    </div>
  );
};

export default ClientManagementSystem;